import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import config from 'constants/config'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { CruiseContentSections, CruiseSectionTabValues } from 'constants/cruise'
import useCruiseContentSectionLabels from 'hooks/Cruise/useCruiseContentSectionLabels'
import useCruiseStartBookingFlow from 'hooks/Cruise/useCruiseStartBookingFlow'
import { replaceWithRegion } from 'actions/NavigationActions'
import CruiseFAQ from './FAQ/CruiseFAQ'
import Pane from 'components/Common/Pane'
import CruiseHeadData from './CruiseHeadData'
import CruiseOverview from './CruiseOverview'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import Heading from 'LuxKit/Typography/Heading'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import { trackCruisesClickEvent } from 'analytics/cruise'
import { offerHasValidDepartures } from 'lib/cruises/cruiseUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CruiseVerticalUSPs from './BookWithConfidence/CruiseVerticalUSPs'
import CruiseStickyFooter from './StickyElements/CruiseStickyFooter'
import useQueryParams from 'hooks/useQueryParams'
import { replace } from 'lib/routing/appRouting'
import CruiseStickyHeader from './StickyElements/CruiseStickyHeader'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import { getIsCruiseOfferEarlyTravellerSelectionEnabled } from 'selectors/cruiseOfferSelectors'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import CruiseRecommendations from 'components/Common/OfferRecommendations/CruiseRecommendations'
import CruiseSoldoutCard from 'components/OfferPage/Cruises/CruiseSoldoutCard'
import CruiseOfferPageDepartures from './Departures/CruiseOfferPageDepartures'
import CruiseOfferTravellerSelectionModal, { type CruiseOfferTravellerSelectionModalResult } from 'checkout/Components/Cruises/CheckoutV2/TravellerSelectionPicker/CruiseOfferTravellerSelectionModal'
import ModalContext from 'contexts/ModalContext'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { hasCruiseBundleRates } from 'lib/cruises/cruiseBookingFlow'
import CruiseOfferPageItinerary from './CruiseItinerary/CruiseOfferPageItinerary'
import CruiseShip from './CruiseShip/CruiseShip'
import CruiseAlertBanner from './CruiseAlertBanner'
import OfferPageGallery from 'components/OfferPage/OfferPageGallery'
import { usePlaceByName } from 'hooks/usePlace'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import Group from 'components/utils/Group'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import useHasGotToAnchor from 'hooks/useHasGotToAnchor'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import ShareModal from 'components/Common/Modals/ShareModal'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import useShareModalConfig from 'hooks/useShareModalConfig'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useRebrandVersion from 'hooks/useRebrandVersion'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import CruiseFinePrint from './FinePrint/CruiseFinePrint'
import SiteTakeoverOfferPageBanner from 'takeovers/utils/components/SiteTakeoverOfferPageBanner'
import CruiseContentTabs from './CruiseContentTabs'
import { dateDifference } from 'lib/datetime/dateUtils'
import { isVelocityEnabled } from 'selectors/featuresSelectors'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import { generateCruiseCheckoutItem } from 'checkout/lib/utils/cruises/cart'
import WeddingGiftRegistryCruiseCabinTypeModal, { type WeddingGiftRegistryCruiseCabinTypeModalResult } from 'weddings/components/WeddingGiftRegistryCruiseCabinTypeModal'
export { getCruiseInitialCheckoutStep, getCruiseInitialOccupancy, hasCruiseBundleRates } from 'lib/cruises/cruiseBookingFlow'

const USPPane = styled(Pane)`
  padding: ${rem(40)} 0;
`
const MainContent = styled(Group)`
  flex-grow: 1;
`

const SectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(32)};
  padding-top: ${rem(32)};
`

const OverviewSectionContainer = styled(LayoutContainer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};
`

type SectionTabs = {
  label: string
  id: CruiseSectionTabValues
}

const CRUISE_OFFER_PAGE_OCCUPANCY_INITIAL: Array<App.Occupants> = [{
  adults: 2,
  infants: 0,
  children: 0,
  teenagers: 0,
  childrenAge: [],
  childrenBirthDate: [],
}]

interface Props {
  cruiseItem: App.Checkout.CruiseItem
  isSpoofed: boolean
  offer: App.CruiseOffer
  isVelocityEnabled: boolean
  currentRegionCode: string
}

function determineCruiseContentSections(
  offer: App.CruiseOffer,
): Set<CruiseContentSections> {
  const sections = new Set<CruiseContentSections>()

  if (offer.images.length) {
    sections.add(CruiseContentSections.GALLERY)
  }

  sections.add(CruiseContentSections.OVERVIEW)

  if (offer.itinerary?.length) {
    sections.add(CruiseContentSections.ITINERARY)
  }

  if (offer.ship?.cabinCategories?.length && offer.ship?.decks?.length) {
    sections.add(CruiseContentSections.DECKS_AND_CABINS)
  }

  if (offer.finePrint?.sections.length) {
    sections.add(CruiseContentSections.FINE_PRINT)
  }

  if (offer.ship.shipInfo?.length && offer.cruiseLine.vendorInfo?.length) {
    sections.add(CruiseContentSections.FAQ)
  }

  return sections
}

function CruiseContent({
  cruiseItem,
  offer,
  currentRegionCode,
  isSpoofed,
}: Props) {
  const intl = useIntl()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const sectionLabels = useCruiseContentSectionLabels()
  const { itinerary, departures, mainDepartureDetails } = offer
  const { lowestOverallPriceDetails } = mainDepartureDetails
  const {
    isNew,
    inclusionEndDate,
    lowestPrice,
    memberLowestPrice,
  } = useCruiseOfferView({ offer })

  const shareConfig = useShareModalConfig({
    offerId: offer.id,
    location: offer.name,
    type: 'offer',
    offerType: 'cruise',
  })
  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const departuresDatesAnchor = useRef<HTMLDivElement>(null)

  const dispatch = useAppDispatch()
  const params = useQueryParams()
  const showModal = useContext(ModalContext)
  const { isActive: isWeddingGiftRegistry, addItems: addItemsToWeddingGiftRegistryCart } = useWeddingGiftRegistryContext()

  const reachedTabsSection = useHasGotToAnchor(departuresDatesAnchor.current, -70, 25)
  const [currentSectionTab, setCurrentSectionTab] = useState<CruiseSectionTabValues>(CruiseSectionTabValues.DATES_PRICING)
  const [offerPageOccupancies, setOfferPageOccupancies] = useState<Array<App.Occupants>>(CRUISE_OFFER_PAGE_OCCUPANCY_INITIAL)
  const [hasAppliedOfferPageOccupancy, setHasAppliedOfferPageOccupancy] = useState(false)

  const datesPricingTabActive = currentSectionTab === CruiseSectionTabValues.DATES_PRICING
  useExperiment(Experiments.cruiseOfferPageEarlyTravellerSelection, {
    canActivate: datesPricingTabActive && !isSpoofed,
  })
  const isCruiseOfferTravellerPickerVariant = useAppSelector(getIsCruiseOfferEarlyTravellerSelectionEnabled)
  const contentSections = useMemo(
    () => determineCruiseContentSections(offer),
    [offer],
  )

  const departurePortPlace = usePlaceByName(offer.departurePort)

  const { startBookingWithOccupancies, startBookingFlow } = useCruiseStartBookingFlow({
    offer,
    cruiseItem,
  })

  const onOfferPageOccupancyApplied = useCallback(() => {
    setHasAppliedOfferPageOccupancy(true)
  }, [])

  useEffect(() => {
    setHasAppliedOfferPageOccupancy(false)
  }, [offer.id])

  const onHandleSelectDeparture = useCallback(async(departure: App.CruiseDepartureDetails) => {
    if (!departure.departureDate || !departure.arrivalDate) return

    if (isWeddingGiftRegistry) {
      const dates = {
        startDate: departure.departureDate,
        endDate: departure.arrivalDate,
      }

      let occupancies = offerPageOccupancies
      if (occupancies.length === 0) {
        occupancies = CRUISE_OFFER_PAGE_OCCUPANCY_INITIAL
      }

      const cabinTypeOptions = Object.values(offer.lowestDeparturePricesByCategory[departure.id] ?? {})
        .filter(price => !!price)
        .sort((left, right) => left.total - right.total)
        .map((price) => {
          const cabinCategory = offer.ship.cabinCategories?.find(category => category.cabinType.toLowerCase() === price.cabinCategory.toLowerCase())
          return {
            cabinType: price.cabinCategory,
            price,
            imageId: cabinCategory?.imageId,
          }
        })

      if (cabinTypeOptions.length === 0) {
        return
      }

      const selectedCabinType = await showModal<WeddingGiftRegistryCruiseCabinTypeModalResult | undefined>(
        <WeddingGiftRegistryCruiseCabinTypeModal
          options={cabinTypeOptions}
          occupancies={occupancies}
          offer={offer}
          departureId={departure.id}
        />,
      )

      if (!selectedCabinType) {
        return
      }

      const items = selectedCabinType.rateSelections.map((rateSelection) => {
        const occupancy = rateSelection.occupancy
        const travellerCount = occupancy.adults + (occupancy.children ?? 0) + (occupancy.teenagers ?? 0)
        const giftRegistryPrice = selectedCabinType.price.total * travellerCount

        return {
          ...generateCruiseCheckoutItem({
            version: 'v2',
            offerId: offer.id,
            departureId: departure.id,
            dates,
            occupancy: rateSelection.occupancy,
          }),
          sessionId: rateSelection.sessionId,
          bookingId: rateSelection.bookingId,
          cabinType: selectedCabinType.cabinType,
          componentId: rateSelection.cabinRate.componentId,
          luxPlusInclusionsByTier: rateSelection.cabinRate.luxPlusInclusionsByTier,
          standardInclusions: rateSelection.cabinRate.standardInclusions,
          packageDescription: rateSelection.cabinRate.description,
          price: giftRegistryPrice,
          memberPrice: 0,
          isAddedToCart: true,
        }
      })

      addItemsToWeddingGiftRegistryCart(items)
      return
    }

    if (hasCruiseBundleRates(offer)) {
      startBookingFlow(departure)
      return
    }

    if (isCruiseOfferTravellerPickerVariant) {
      if (hasAppliedOfferPageOccupancy) {
        startBookingWithOccupancies(
          departure,
          offerPageOccupancies,
          false,
        )
        return
      }

      const result = await showModal<CruiseOfferTravellerSelectionModalResult | undefined>(
        <CruiseOfferTravellerSelectionModal
          offer={offer}
          departure={departure}
          initialOccupancies={offerPageOccupancies}
        />,
      )
      if (result) {
        setOfferPageOccupancies(result.occupancies)
        startBookingWithOccupancies(
          departure,
          result.occupancies,
          result.interestInConnectedCabins,
        )
      }
      return
    }

    startBookingFlow(departure)
  }, [
    addItemsToWeddingGiftRegistryCart,
    hasAppliedOfferPageOccupancy,
    isWeddingGiftRegistry,
    isCruiseOfferTravellerPickerVariant,
    offer,
    offerPageOccupancies,
    showModal,
    startBookingWithOccupancies,
    startBookingFlow,
  ])

  const scrollToPackageOptions = useCallback((tab: string) => {
    if (tab === 'dates-and-pricing') {
      handleTabsChange(CruiseSectionTabValues.DATES_PRICING)
    }
    departuresDatesAnchor.current?.scrollIntoView({
      behavior: 'smooth',
    })
  }, [])

  const hasValidDepartures = useMemo(() => offerHasValidDepartures(offer), [offer])

  const SECTION_TABS: Array<SectionTabs> = useMemo(() => {
    const tabs: Array<SectionTabs> = [
      {
        label: intl.formatMessage({
          id: '2SFmPHzouKxyR2utDrKE',
          defaultMessage: 'Dates & Pricing',
          description: 'Tab label for dates and pricing section in the date selection or calendar picker',
        }),
        id: CruiseSectionTabValues.DATES_PRICING,
      },
      {
        label: intl.formatMessage({
          id: 'eUwovuumZR8iNkTz7vTp',
          defaultMessage: 'Itinerary',
          description: 'Tab label for itinerary section on the itinerary or schedule view',
        }),
        id: CruiseSectionTabValues.ITINERARY,
      },
      {
        label: intl.formatMessage({
          id: '2mNbeTs8gDqJkL9e4e6X',
          defaultMessage: 'Ship & Cabins',
          description: 'Tab label for about the ship section in the cruises section',
        }),
        id: CruiseSectionTabValues.ABOUT_SHIP,
      },
      {
        label: intl.formatMessage({
          id: 'I1xB1k-Ue6Yu2HIlhhUY',
          defaultMessage: 'Fine Print',
          description: 'Tab label for fine print section in the cruises section',
        }),
        id: CruiseSectionTabValues.FINE_PRINT,
      },
    ]
    if (contentSections.has(CruiseContentSections.FAQ)) {
      tabs.push({
        label: intl.formatMessage({
          id: '7FUPVopNIRxB5VZWkOEK',
          defaultMessage: 'FAQs',
          description: 'Navigation tab label for the frequently asked questions section on the offer page',
        }),
        id: CruiseSectionTabValues.FAQ,
      })
    }
    return tabs
  }, [contentSections, intl])

  const handleTabsChange = (tab: CruiseSectionTabValues) => {
    trackCruisesClickEvent(`Cruise offer page - ${tab}`)
    setCurrentSectionTab(tab)
  }

  const recommendationFilters = useMemo<App.OfferListFilters>(() => {
    return {
      offerTypes: ['cruise'],
      departurePlaceId: departurePortPlace?.id,
      cruiseLines: [offer.cruiseLine.code!],
    }
  }, [offer, departurePortPlace?.id])

  const promotionEndingDate = useMemo(() => {
    return inclusionEndDate ? dateDifference(new Date(inclusionEndDate), new Date()).days : undefined
  }, [inclusionEndDate])

  useEffect(() => {
    const availableCruiseRegions = config.CRUISES_REGIONS?.split(',') ?? []
    if (!availableCruiseRegions.includes(currentRegionCode)) {
      dispatch(replaceWithRegion('/'))
    }
  }, [dispatch, currentRegionCode])

  useEffect(() => {
    if (params.getAll('departureMonths').length > 0) {
      // remove params on page landing, it was only for initialisation
      const nextParams = new URLSearchParams(params)
      nextParams.delete('departureMonths')

      if (params.get('scrollToPackages')) {
        nextParams.delete('scrollToPackages')
        scrollToPackageOptions('dates-and-pricing')
      }

      dispatch(replace({ search: nextParams.toString() }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const shouldShowBanner = useShouldShowBanner({ location: 'offer', vertical: 'cruise', offer })
  const shouldShowToolTip = useScreenSizeUp('tablet')

  return (<div>
    <CruiseHeadData offer={offer} />
    <CruiseStickyHeader
      onView={scrollToPackageOptions}
      offer={offer}
      priceFrom={lowestOverallPriceDetails}
      price={lowestPrice}
      memberPrice={memberLowestPrice}
      visible={reachedTabsSection}
      title={offer.name}
      tabs={SECTION_TABS}
      currentSectionTab={currentSectionTab}
      hasValidDepartures={hasValidDepartures}
      handleTabsChange={handleTabsChange}
    />
    <div id="CRUISE_CONTENT_TOP_ANCHOR" aria-hidden="true"></div>
    {shouldShowBanner && <SiteTakeoverOfferPageBanner vertical="cruise" />}

    <MainContent direction="vertical" verticalAlign="space-between">
      <div>
        {!hasValidDepartures && <Pane>
          <CruiseSoldoutCard offer={offer} />
        </Pane>}

        <CruiseAlertBanner offer={offer} />

        {contentSections.has(CruiseContentSections.GALLERY) && (
          <OfferPageGallery
            offer={offer}
            shareModalConfig={shareConfig}
          />
        )}

        <Pane>
          <OverviewSectionContainer>
            <VerticalSpacer gap={8}>
              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
                <Group direction="horizontal" gap={8}>
                  <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
                  {!!isNew && <UrgencyLabel variant="speed">{intl.formatMessage({
                    id: 'JdWavSNhirIPsvG32KSz',
                    defaultMessage: 'NEW',
                    description: 'Badge label indicating that an offer listing is newly available on the platform',
                  })}
                  </UrgencyLabel>}
                  {!!promotionEndingDate && <OfferUrgencyCountdownLabel endDate={inclusionEndDate ?? ''} tagPlace="cruise-content" />}
                </Group>

                <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
                  {!!shareConfig && (
                    <ShareModal shareConfig={shareConfig} openOnHash="#share">
                      <ShareWithFriendsButton showToolTip={shouldShowToolTip} />
                    </ShareModal>
                  )}
                  <TripGuard>
                    <BookmarkButton offer={offer} />
                  </TripGuard>
                </Group>
              </Group>
              <CruiseOverview
                offer={offer}
                showPackageOptions={() => scrollToPackageOptions('dates-and-pricing')}
              />
            </VerticalSpacer>
          </OverviewSectionContainer>
        </Pane>
      </div>
      <div>
        <Pane>
          <ScrollAnchor ref={departuresDatesAnchor} />
          <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="cruise" saveUpToPercent={offer.mainDepartureDetails.lowestOverallPriceDetails.discountPills?.discountPercentage} />}>
            <LayoutContainer>
              <CruiseContentTabs
                tabs={SECTION_TABS}
                onTabsChange={handleTabsChange}
                activeTab={currentSectionTab}
              />
            </LayoutContainer>
          </SoftOfferPageWalledGarden>
        </Pane>
        <SoftOfferPageWalledGarden>
          {currentSectionTab === 'DATES_PRICING' && (
            <CruiseOfferPageDepartures
              offer={offer}
              departuresByDate={departures}
              hasValidDepartures={hasValidDepartures}
              onHandleSelectDeparture={onHandleSelectDeparture}
              offerPageOccupancies={offerPageOccupancies}
              setOfferPageOccupancies={setOfferPageOccupancies}
              onOfferPageOccupancyApplied={onOfferPageOccupancyApplied}
              isCruiseOfferTravellerPickerVariant={isCruiseOfferTravellerPickerVariant}
              isWeddingGiftRegistry={isWeddingGiftRegistry}
            />
          )}

          {currentSectionTab === 'ITINERARY' && contentSections.has(CruiseContentSections.ITINERARY) && (
            <CruiseOfferPageItinerary
              offerId={offer.id}
              itinerary={itinerary}
              cruiseType={offer.cruiseType}
              offerFallbackImageId={offer.heroImageId ?? offer.images[0]?.id}
            />
          )}

          {currentSectionTab === 'ABOUT_SHIP' && (
            <CruiseShip
              offer={offer}
              contentSections={contentSections}
              reachedTabsSection={reachedTabsSection}
              scrollToPackageOptions={scrollToPackageOptions}
            />
          )}

          {currentSectionTab === 'FINE_PRINT' && (
            <CruiseFinePrint
              cruiseLine={offer.cruiseLine}
              bundleTermsAndConditions={offer.bundle?.termsAndConditions}
            />
          )}

          {currentSectionTab === 'FAQ' && (
            <Pane>
              <SectionContainer size="md">
                <Group direction="vertical" gap={48}>
                  <Heading
                    align="center"
                    variant="heading2"
                    className="section-title"
                  >
                    {sectionLabels[CruiseContentSections.FAQ]}
                  </Heading>

                  <div className="section-content" data-testid="faq-section">
                    <CruiseFAQ ship={offer.ship} cruiseLine={offer.cruiseLine} cruiseType={offer.cruiseType} />
                  </div>
                </Group>
              </SectionContainer>
            </Pane>
          )}

          {/*
            Rebrand: beige (Neutral / Surface / LightBackground) + default palette (black text).
            Legacy: inverse (black background + white text via normal-contrast).
          */}
          <USPPane
            paletteType={isRebrand ? 'default' : 'inverse'}
            background={isRebrand ? 'light-background' : 'normal-background'}
          >
            <LayoutContainer>
              <Heading colour="normal-contrast" variant="heading3" align="center">
                {intl.formatMessage({
                  id: 'BO1ZPzfNouTWaFhekdTQ',
                  defaultMessage: 'Why you should book with us',
                  description: 'Section heading for why book with us section in the cruises section',
                })}
              </Heading>
              <CruiseVerticalUSPs />
            </LayoutContainer>
          </USPPane>

          <CruiseStickyFooter
            visible={!reachedTabsSection}
            onView={scrollToPackageOptions}
            priceFrom={lowestOverallPriceDetails}
            price={lowestPrice}
            memberPrice={memberLowestPrice}
            hasValidDepartures={hasValidDepartures}
            offer={offer}
          />
          {recommendationFilters.departurePlaceId && <ErrorBoundary fallback={null}>
            <CruiseRecommendations
              offer={offer}
              filters={recommendationFilters}
              cruiseLineName={offer.cruiseLine.name}
              departurePortName={offer.departurePort}
            />
          </ErrorBoundary>}
        </SoftOfferPageWalledGarden>
      </div>
    </MainContent>
  </div>
  )
}

function mapStateToProps(appState: App.State) {
  return {
    cruiseItem: getCruiseItems(appState)[0],
    isSpoofed: appState.auth.account.isSpoofed,
    isVelocityEnabled: isVelocityEnabled(appState),
    currentRegionCode: appState.geo.currentRegionCode,
    place: appState.destination.locationToPlace,
  }
}

export default connect(mapStateToProps)(CruiseContent)
